/**
 * Maps simulation weather into the colour palette the sky, ocean and lighting share.
 * One place decides what "overcast North Atlantic at 10:30" looks like, so the sky, the
 * sea's reflection of it, and the fog that dissolves the horizon can never disagree.
 */

import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { WeatherState } from '../sim/weather';
import { daylightFactor, sunDirection } from '../sim/weather';
import { clamp01, lerp } from '../sim/math';

export interface Environment {
  zenithColor: Color3;
  horizonColor: Color3;
  sunColor: Color3;
  sunDir: Vector3;
  sunGlowPower: number;
  sunGlowIntensity: number;
  horizonFalloff: number;

  /** Low-frequency cloud state used by the dome-independent PBR environment cube. */
  cloudCover: number;
  storminess: number;
  cloudWindX: number;
  cloudWindZ: number;

  deepColor: Color3;
  shallowColor: Color3;
  scatterColor: Color3;
  scatterStrength: number;

  foamColor: Color3;
  foamAmount: number;

  fogColor: Color3;
  /** Tuned so that 1/uFogDensity is roughly the distance at which haze dominates. */
  fogDensity: number;
  /**
   * Ambient mist density, 1/m — the uniform scattering of sea air itself. Sized for the
   * rig's own 15-250m depth range: at overcast defaults the crane tip (~55m) softens a
   * few percent while the near rail stays crisp, and the gradient is continuous between.
   */
  mistAirDensity: number;
  /**
   * Dense, short-ranged air right around the platform.
   *
   * Separate from the atmosphere proper because the two are set by different things: the
   * air densities are set by how far you must see across open water, which leaves the rig
   * itself rendering almost crisp. This grades the near structure without reaching a
   * contact kilometres out.
   */
  mistNearDensity: number;
  /** Distance over which the near-field term accumulates before saturating, metres. */
  mistNearRange: number;

  /**
   * Hue the sea is pushed toward in the rig's immediate footprint, and how far that
   * reaches in metres.
   *
   * The water under a platform is not the water half a kilometre out. It sits in the
   * structure's shadow, churned and aerated by the legs, and it goes black — the deep
   * green cast is what little light gets back out of it. Without this the sea directly
   * below simply carried whatever the floodlights put on it, which at night meant an
   * amber wash that read as mud.
   */
  nearWaterTint: Color3;
  nearWaterRange: number;
  /**
   * Scale height of the ambient haze, metres. Only the sky integral needs it: over a few
   * hundred metres of geometry the ambient term is effectively uniform, but a sky ray runs
   * to infinity and needs a finite atmosphere to converge against.
   *
   * This is the marine AEROSOL layer, not the full boundary layer. Sized at ~1km the
   * vertical column becomes so thick that the zenith washes to flat grey and the storm
   * deck disappears; a few hundred metres keeps cloud structure overhead while still
   * closing the horizon, which is what a hazy day actually looks like.
   */
  mistAirHeightScale: number;
  /**
   * Artistic weighting on the SKY's share of the mist integral, 0..1.
   *
   * Taken literally, the plane-parallel integral is correct — a hazy day really does wash
   * the sky toward flat grey well above the horizon. But the storm deck is the most
   * dramatic element in the frame, and dissolving it costs more than the added realism
   * buys. This deliberately under-applies mist to the dome so cloud structure survives
   * overhead while the horizon still closes. Geometry and water are NOT weakened: the
   * distance grading Adam asked for stays fully physical.
   */
  skyMistStrength: number;
  /** Extra density of the spray/evaporation layer hugging the water, 1/m. */
  mistSeaDensity: number;
  /** Scale height of that layer, metres. */
  mistHeightScale: number;
  horizonHaze: number;

  ambientColor: Color3;
  ambientIntensity: number;
  directIntensity: number;

  /** Shared rig floodlight palette and night envelope for steel and ocean lighting. */
  rigLightColor: Color3;
  rigLightStrength: number;

  /** Overall exposure for the tone mapper. */
  exposure: number;
  detailStrength: number;
}

const c = (r: number, g: number, b: number): Color3 => new Color3(r, g, b);

/** Palette anchors. Everything else is a blend of these four. */
const SKY_DAY_CLEAR = { zenith: c(0.16, 0.34, 0.66), horizon: c(0.70, 0.81, 0.92) };
const SKY_DAY_OVERCAST = { zenith: c(0.072, 0.083, 0.102), horizon: c(0.28, 0.30, 0.33) };
/*
 * These night anchors were never the problem, and lowering them was the wrong fix.
 *
 * The night sky read as bright slate because the fog term below mixed a *daylight*
 * scattering grey into it at full strength regardless of the hour — see `scatterGrey`.
 * That lifted the horizon roughly three and a half times above what this palette asks
 * for, and parked the whole dome in the low mid-tones where its smooth cloud gradients
 * land on a handful of 8-bit output levels and posterise into hard contours. With the
 * scattering term correctly tied to daylight, the original values land the moonless sky
 * around 15% brightness: dark, but still weather rather than a black screen.
 */
const SKY_NIGHT_CLEAR = { zenith: c(0.006, 0.012, 0.030), horizon: c(0.030, 0.045, 0.075) };
const SKY_NIGHT_OVERCAST = { zenith: c(0.008, 0.010, 0.016), horizon: c(0.026, 0.030, 0.042) };

/** Warm low-sun tint applied around sunrise and sunset. */
const DUSK_TINT = c(1.0, 0.52, 0.28);

function mixColor(a: Color3, b: Color3, t: number): Color3 {
  return new Color3(lerp(a.r, b.r, t), lerp(a.g, b.g, t), lerp(a.b, b.b, t));
}

/** 1 near sunrise/sunset, 0 at midday and deep night. */
function twilightFactor(timeOfDay: number): number {
  const t = ((timeOfDay % 24) + 24) % 24;
  const dawn = 1 - clamp01(Math.abs(t - 6.5) / 2.2);
  const dusk = 1 - clamp01(Math.abs(t - 19.5) / 2.4);
  return Math.max(dawn, dusk);
}

export function computeEnvironment(w: WeatherState): Environment {
  const day = daylightFactor(w.timeOfDay);
  const cloud = clamp01(w.cloudCover);
  const twilight = twilightFactor(w.timeOfDay);
  const fog = clamp01(w.fogDensity);
  const storminess = clamp01((w.seaState - 4) / 3);
  // Shared mist ramp. Subtracting a floor before the curve is what lets the clearest
  // preset reach genuinely thin air rather than merely less-thick air.
  const mistFactor = Math.pow(clamp01((fog - 0.08) / 0.92), 1.1);

  // Sky: blend clear<->overcast, then night<->day.
  const dayZenith = mixColor(SKY_DAY_CLEAR.zenith, SKY_DAY_OVERCAST.zenith, cloud);
  const dayHorizon = mixColor(SKY_DAY_CLEAR.horizon, SKY_DAY_OVERCAST.horizon, cloud);
  const nightZenith = mixColor(SKY_NIGHT_CLEAR.zenith, SKY_NIGHT_OVERCAST.zenith, cloud);
  const nightHorizon = mixColor(SKY_NIGHT_CLEAR.horizon, SKY_NIGHT_OVERCAST.horizon, cloud);

  let zenith = mixColor(nightZenith, dayZenith, day);
  let horizon = mixColor(nightHorizon, dayHorizon, day);

  // Twilight warms the horizon band strongly and the zenith slightly.
  if (twilight > 0.001) {
    const warmth = twilight * (1 - cloud * 0.55);
    horizon = mixColor(horizon, DUSK_TINT, warmth * 0.55);
    zenith = mixColor(zenith, DUSK_TINT, warmth * 0.12);
  }

  // Heavy weather drains saturation and darkens everything.
  const gloom = storminess * cloud;
  zenith = zenith.scale(lerp(1, 0.55, gloom));
  horizon = horizon.scale(lerp(1, 0.68, gloom));

  /*
   * Fog lifts the horizon toward a flat grey and mutes the zenith — but only while there
   * is sunlight for it to scatter.
   *
   * This grey was applied at full strength regardless of the hour, so a moonless night
   * with ordinary marine haze had a daylight grey mixed into its horizon and came out
   * three times brighter than the palette specified. That single term was most of why the
   * night sky read as bright slate rather than darkness, and it dragged the whole dome up
   * into the tonal region where its gradients band.
   */
  const scatterGrey = lerp(0.05, 1, day);
  const fogGrey = new Color3(
    lerp(horizon.r, 0.66 * scatterGrey, 0.5) * lerp(1, 0.9, gloom),
    lerp(horizon.g, 0.68 * scatterGrey, 0.5) * lerp(1, 0.9, gloom),
    lerp(horizon.b, 0.70 * scatterGrey, 0.5) * lerp(1, 0.92, gloom),
  );
  horizon = mixColor(horizon, fogGrey, fog * 0.65);

  const sd = sunDirection(w.timeOfDay);
  const sunDir = new Vector3(sd.x, sd.y, sd.z).normalize();

  // Sun/moon colour and how tight its bloom is. Cloud spreads it into a broad smear.
  const nightLight = c(0.30, 0.36, 0.52);
  const dayLight = mixColor(c(1.0, 0.97, 0.92), DUSK_TINT, twilight * 0.6);
  const sunColor = mixColor(nightLight, dayLight, day).scale(lerp(0.55, 0.10, cloud) + 0.05);

  const sunGlowPower = lerp(220, 5.5, cloud);
  const sunGlowIntensity = lerp(1.2, 0.42, cloud) * lerp(0.35, 1, day + 0.15);

  // Water body. Under overcast the sea is a dark slate that only brightens by reflection.
  const waterDark = mixColor(c(0.0085, 0.0175, 0.0245), c(0.003, 0.006, 0.010), 1 - day);
  const deep = mixColor(waterDark, c(0.010, 0.024, 0.034), day * (1 - cloud * 0.4));
  const shallow = mixColor(
    c(0.016, 0.030, 0.038).scale(lerp(0.25, 1, day)),
    c(0.042, 0.078, 0.098).scale(lerp(0.25, 1, day)),
    1 - cloud * 0.35,
  );
  // Green-glass forward scatter through thin crests.
  const scatter = mixColor(c(0.03, 0.09, 0.085), c(0.075, 0.19, 0.165), day).scale(lerp(0.4, 1, day));

  // Foam is the brightest thing in the frame under overcast; it must not blow out.
  const foamBase = mixColor(c(0.22, 0.25, 0.29), c(0.61, 0.64, 0.68), day);
  const foamColor = mixColor(foamBase, horizon, 0.10);
  // Rougher seas and stronger wind mean far more whitecap coverage.
  const foamAmount = lerp(0.55, 1.85, storminess) * lerp(0.9, 1.35, clamp01(w.windSpeed / 25));

  // Fog colour tracks the horizon so the sea dissolves into the sky, never onto a line.
  const fogColor = mixColor(horizon, zenith, 0.12);

  // Density is in 1/metres; the fragment shader squares the product for an exp2 falloff.
  // Baseline is deliberately non-zero even in the clearest weather: marine air is never
  // optically empty, and a perfectly crisp horizon is one of the strongest tells that a
  // rendered ocean is not a photograph.
  /*
   * Calibrated against the ranges the game is actually played at.
   *
   * Set so you can see what you are shooting at, and no further.
   *
   * Creative direction is explicit that vessels need not be visible at long range — the
   * radar and the scope's range readout are the instruments for finding them, and the
   * ocean keeping its weather is worth more than a legible horizon. So this is not tuned
   * to make a ship readable at 5 km. It is tuned so a hull inside the gun's working
   * envelope (out to about 2.5 km) is present and graded rather than a shape in soup,
   * and everything past that dissolves into the weather where the instruments take over.
   *
   * The original values were set by eye on the rig, where nothing is further than 200 m,
   * so they were never tested against distance at all: a hull was half gone at 700 m.
   */
  const fogDensity = lerp(0.000052, 0.000165, fog) * lerp(1, 1.35, storminess);
  const horizonHaze = lerp(0.28, 0.62, fog);

  const ambientColor = mixColor(zenith, horizon, 0.6);
  const ambientIntensity = lerp(0.14, 1.15, day) * lerp(1, 0.9, gloom);
  const directIntensity = lerp(0.05, 1.15, day) * lerp(1, 0.55, cloud);
  // Fixtures retain a dim visible lens by day, but their cast light belongs to darkness.
  // Keep the 0..1 night envelope here so every consumer agrees on when artificial light
  // takes authority from the sky.
  const rigLightStrength = clamp01(1 - day * 1.4);
  const rigLightColor = c(1.0, 0.68, 0.38);

  return {
    zenithColor: zenith,
    horizonColor: horizon,
    sunColor,
    sunDir,
    sunGlowPower,
    sunGlowIntensity,
    horizonFalloff: lerp(3.2, 5.5, cloud),

    cloudCover: cloud,
    storminess,
    cloudWindX: Math.sin(w.windDir),
    cloudWindZ: Math.cos(w.windDir),

    deepColor: deep,
    shallowColor: shallow,
    scatterColor: scatter,
    scatterStrength: lerp(0.35, 1.0, day) * lerp(0.6, 1.15, storminess),

    foamColor,
    foamAmount,

    fogColor,
    fogDensity,
    // Ramped so the presets separate the way they should: a clear day is nearly
    // transparent (~9km visual range), Atlantic overcast carries real air (~1.6km), storms
    // considerably more, and a fog bank the most (~570m). The curve is deliberately
    // superlinear off zero — a linear map on fogDensity left clear days visibly hazy,
    // because even the clearest preset carries a non-zero value for horizon softening.
    // Scaled to match: enough air to grade the near structure and dissolve the horizon,
    // not so much that a ship five kilometres out ceases to exist.
    // Trimmed to make room for the near-field term below: you look *through* the thick
    // air around the platform to reach anything distant, so adding it without taking
    // something back would have quietly fogged the whole sea as well.
    mistAirDensity: lerp(0.000023, 0.00086, mistFactor),
    mistNearDensity: lerp(0.00035, 0.0026, mistFactor),
    mistNearRange: 150,

    /*
     * Applied as a multiplier on the water's own luminance, so foam and glints keep their
     * relative brightness and only the hue and level change.
     *
     * Green is barely present — it is a cast on near-black, not a colour. Its weighted sum
     * is about 0.14, so the sea in the rig's footprint carries roughly a seventh of the
     * light it otherwise would: black water first, with just enough green left in it to
     * say the black is water and not a hole in the frame.
     */
    nearWaterTint: c(0.115, 0.144, 0.129),
    nearWaterRange: 300,
    // Spray from breaking crests, so this one keeps a direct storminess term.
    mistSeaDensity: lerp(0.000033, 0.00059, mistFactor) + storminess * 0.00018,
    mistAirHeightScale: lerp(360, 205, mistFactor),
    // Fog banks are allowed to close the sky; ordinary haze must not.
    skyMistStrength: lerp(0.25, 0.95, mistFactor),
    mistHeightScale: 34,
    horizonHaze,

    ambientColor,
    ambientIntensity,
    directIntensity,

    rigLightColor,
    rigLightStrength,

    exposure: lerp(3.5, 2.95, day),
    detailStrength: lerp(0.55, 0.85, storminess),
  };
}
